import { useRef, useState } from 'react'

const STORAGE_KEY = 'operacao:task-column-widths:v1'
const MIN_WIDTHS = [32, 200, 110, 140, 140, 85, 170, 32]
const MAX_WIDTH = 900
const clamp = (width, index) => Math.round(Math.max(MIN_WIDTHS[index], Math.min(MAX_WIDTH, width)))
const save = widths => {
  try {
    if (widths) localStorage.setItem(STORAGE_KEY, JSON.stringify(widths))
    else localStorage.removeItem(STORAGE_KEY)
  } catch { /* O ajuste continua funcionando se o armazenamento estiver indisponível. */ }
}

export default function useTaskColumnWidths() {
  const tableRef = useRef(null)
  const drag = useRef(null)
  const [resizing, setResizing] = useState(false)
  const [widths, setWidths] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY))
      if (Array.isArray(stored) && stored.length === 8 && stored.every(w => typeof w === 'number' && Number.isFinite(w))) {
        return stored.map(clamp)
      }
    } catch { /* Ignora preferências inválidas. */ }
    return null
  })
  const measure = () => Array.from(tableRef.current.tHead.rows[0].cells, (cell, index) => clamp(cell.getBoundingClientRect().width, index))
  const finish = (event, cancelled = false) => {
    const active = drag.current
    if (!active || active.pointerId !== event.pointerId) return
    drag.current = null
    setResizing(false)
    if (cancelled) setWidths(active.previous)
    else save(active.latest)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }
  const handle = (index, label) => <button
    type="button"
    className="ot-column-resize"
    aria-label={`Ajustar largura da coluna ${label}`}
    title="Arraste para ajustar. Use as setas do teclado. Duplo clique restaura as larguras."
    onClick={event => event.stopPropagation()}
    onDoubleClick={() => { setWidths(null); save(null) }}
    onPointerDown={event => {
      if (event.button !== 0 || drag.current) return
      event.preventDefault()
      event.stopPropagation()
      const initial = measure()
      drag.current = { pointerId: event.pointerId, x: event.clientX, index, initial, previous: widths, latest: initial }
      event.currentTarget.setPointerCapture(event.pointerId)
      setWidths(initial)
      setResizing(true)
    }}
    onPointerMove={event => {
      const active = drag.current
      if (!active || active.pointerId !== event.pointerId) return
      const next = [...active.initial]
      next[index] = clamp(active.initial[index] + event.clientX - active.x, index)
      active.latest = next
      setWidths(next)
    }}
    onPointerUp={event => finish(event)}
    onPointerCancel={event => finish(event, true)}
    onLostPointerCapture={event => finish(event, true)}
    onKeyDown={event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()
      const next = measure()
      const step = event.shiftKey ? 40 : 10
      next[index] = event.key === 'Home' ? MIN_WIDTHS[index] : event.key === 'End' ? MAX_WIDTH : clamp(next[index] + (event.key === 'ArrowRight' ? step : -step), index)
      setWidths(next)
      save(next)
    }}
  />
  return { tableRef, widths, resizing, handle, tableStyle: widths ? { width: widths.reduce((sum, w) => sum + w, 0), minWidth: 0 } : undefined }
}
